import { Attribute, Relationship, Resource } from 'json-api';
import { Translatable } from 'src/app/translation/translatable';
import { AtomResource } from '../classes/atom-resource';
import { Activity } from './activity.resource';
import { Contact, ContactConfig } from './contact.resource';
import { Contract } from './contract.resource';
import { Country } from './country.resource';
import { Job } from './job.resource';
import { Offer } from './offer.resource';
import { Product } from './product.resource';
import { Service } from './service.resource';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import {
  NumberFilter,
  StringFilter,
  ResourceListFilter,
} from 'src/app/filter/filter';
import { AtomService } from '../atom.service';
import {
  CountryConfig,
  FirmConfig,
} from 'src/app/shared/tools/search/search-config';
import { Trademark, TrademarksConfig } from './trademark.resource';
import { JobsIpRange } from './ip-range';
import { JobsIPRangesVersionsConfig } from './jobsiprangesversions.resource';

@Resource({
  type: 'firms',
})
export class Firm extends AtomResource {
  @Attribute() name?: Translatable;
  @Attribute({ key: 'official_name' }) officialName?: Translatable;
  @Attribute({ key: 'didomi_consent_id' }) didomiConsentId?: string;
  @Attribute({ key: 'vat_number' }) vatNumber?: string;

  @Relationship() headquarterscountry?: Country;
  @Relationship() trademarks?: Trademark[];
  @Relationship() contacts?: Contact[];
  @Relationship() jobs?: Job[];
  @Relationship() parentfirm?: Firm;
  @Relationship() childfirms?: Firm[];
  @Relationship() contracts?: Contract[];
  @Relationship() offers?: Offer[];
  @Relationship() products?: Product[];
  @Relationship() services?: Service[];

  get ISPJob(): Job | undefined {
    return this.jobs?.find(({ activity }) => {
      if (!activity) {
        throw new Error('You forgot to include job activity');
      }
      return activity.isISP;
    });
  }

  get MNOJob(): Job | undefined {
    return this.jobs?.find(({ activity }) => {
      if (!activity) {
        throw new Error('You forgot to include job activity');
      }
      return activity.isMNO;
    });
  }

  get paymentJob(): Job | undefined {
    return this.jobs?.find(({ activity }) => {
      if (!activity) {
        throw new Error('You forgot to include job activity');
      }
      return activity.isPayment;
    });
  }

  hasActivity({ id }: Activity): boolean | undefined {
    return this.jobs?.some((job) => job && job.activity?.id === id);
  }
}
export class FirmsConfig implements ResourceConfig {
  tables = {
    includes: ['headquarterscountry', 'parentfirm.headquarterscountry'],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main', translatable: false }],
        header: 'ID',
        translatable: false,
        displayed: true,
        sort: 'id',
      },
      {
        columnDef: 'name',
        displayedDatas: [
          {
            path: ['name'],
            class: 'mat-body-2',
          },
          {
            path: ['officialName'],
            class: 'mat-body-1',
          },
        ],
        header: 'Name / Official Name',
        displayed: true,
        sort: 'name',
        link: {
          detail: true,
        },
      },
      {
        columnDef: 'vat_number',
        displayedDatas: [{ path: ['vatNumber']}],
        header: 'Vat Number',
        translatable: false,
        displayed: true,
        sort: 'vat_number',
        hint: 'Max 100 characters',
      },
      {
        columnDef: 'headquarterscountry',
        displayedDatas: [
          {
            path: ['headquarterscountry.name'],
            class: 'mat-body-2',
            translatable: true,
          },
          {
            path: ['headquarterscountry.id'],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        header: 'Country',
        translatable: true,
        displayed: true,
        link: {
          resource: 'countries',
          filter: 'country.id',
          icon: 'language',
        },
        options: {
          flag: 'headquarterscountry.id',
        },
        sort: 'headquarterscountry.name',
      },
      {
        columnDef: 'parentFirm',
        displayedDatas: [
          {
            path: ['parentfirm.name'],
            class: 'mat-body-2',
            translatable: true,
          },
          {
            path: ['parentfirm.headquarterscountry.id'],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        header: 'Parent Firm',
        displayed: true,
        link: {
          resource: 'firms',
          filter: 'parentfirm.id',
          icon: 'business',
        },
      },
      {
        columnDef: 'contracts',
        header: 'Contracts',
        displayed: true,
        link: {
          resource: 'contracts',
          list: 'contracts',
          target: 'firms',
        },
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        translatable: false,
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'business',
      title: [
        {
          path: ['name'],
          translatable: false,
        },
      ],
      id: true,
    },
    elements: [
      {
        showEmpty: true,
        list: [
          {
            icon: 'business',
            title: 'Official Name',
            APIKey: 'officialName',
            value: {
              path: ['officialName'],
            },
          },
          {
            icon: 'language',
            title: 'Country',
            APIKey: 'headquarterscountry',
            value: {
              path: ['headquarterscountry.name'],
            },
            link: {
              resource: 'countries',
              filter: 'headquarterscountry.id',
            },
            options: {
              flag: 'headquarterscountry.id',
            },
          },
          {
            icon: 'numbers',
            title: 'Didomi ID',
            APIKey: 'didomiConsentId',
            value: {
              path: ['didomiConsentId'],
              translatable: false,
            },
            options: {
              copy: true,
            },
          },
        ],
      },
      {
        title: 'jobs',
        list: [
          {
            type: 'chips',
            APIKey: 'jobs',
            value: {
              path: ['activity.name'],
            },
          },
        ],
      },
      // {
      //   title: 'Platforms',
      //   list: [
      //     {
      //       type: 'chips',
      //       APIKey: 'jobs',
      //       value: {
      //         path: ['activity.name'],
      //       },
      //     },
      //   ],
      // },
    ],
    tabs: [
      {
        title: 'Network Identifiers',
        elements: [
          {
            title: 'ISP',
            list: [
              {
                type: 'chips',
                APIKey: 'ISPJob.internetserviceproviders',
                value: {
                  path: ['name', 'asn'],
                },
                options: {
                  showDefault: {
                    APIKey: 'default',
                  },
                },
              },
            ],
          },
          {
            title: 'MNO',
            list: [
              {
                type: 'chips',
                APIKey: 'MNOJob.mobilenetworkoperators',
                value: {
                  path: ['name', 'mcc', 'mnc'],
                },
                options: {
                  showDefault: {
                    APIKey: 'default',
                  },
                },
              },
            ],
          },
          {
            title: 'Payment Method',
            list: [
              {
                type: 'chips',
                APIKey: 'paymentJob.paymentmethods',
                value: {
                  path: ['name', 'method'],
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Contacts',
        elements: [
          {
            resource: {
              APIKey: 'contacts',
              config: new ContactConfig(),
              multiple: true,
            },
          },
        ],
      },
      {
        title: 'Linked resources',
        elements: [
          {
            showEmpty: true,
            columns: true,
            list: [
              {
                icon: 'business',
                title: 'Child Firms',
                APIKey: 'childfirms',
                link: {
                  resource: 'firms',
                  list: 'childfirms',
                  target: 'parentfirm',
                },
              },
              {
                icon: 'edit',
                title: 'Contracts',
                APIKey: 'contracts',
                link: {
                  resource: 'contracts',
                  list: 'contracts',
                  target: 'firms',
                },
              },
              {
                icon: 'shopping_basket',
                title: 'Services',
                APIKey: 'services',
                link: {
                  resource: 'services',
                  list: 'services',
                  target: 'ownerfirm',
                },
              },
              {
                icon: 'public',
                title: 'Products',
                APIKey: 'products',
                link: {
                  resource: 'products',
                  list: 'products',
                  target: 'service.ownerfirm',
                },
              },
              {
                icon: 'local_offer',
                title: 'Owned Offers',
                APIKey: 'offers',
                link: {
                  resource: 'offers',
                  list: 'offers',
                  target: 'product.service.ownerfirm',
                },
              },
              // {
              //   icon: 'local_offer',
              //   title: 'End User Provider Offers',
              //   APIKey: 'offers',
              //   link: {
              //     resource: 'offers',
              //     list: 'offers',
              //     target: 'enduserproviderjob',
              //   },
              // },
            ],
          },
        ],
      },
      {
        title: 'Trademarks',
        elements: [
          {
            resource: {
              APIKey: 'trademarks',
              config: new TrademarksConfig(),
              multiple: true,
            },
          },
        ],
      },
      {
        title: 'IP Ranges',
        elements: [
          {
            resource: {
              APIKey: 'jobs',
              config: new IPRangeConfig(this.atom),
              multiple: true,
            },
          },
        ],
      },
    ],
  };
  wizard = {
    noCloseAfterCreation: true,
    tabs: [
      {
        title: 'General Informations',
        formGroup: 'general',
        options: {
          create: true,
        },
        list: [
          {
            type: 'text',
            title: 'Name',
            APIKey: 'name',
            formControl: 'name',
            required: true,
            translatable: true,
          },
          {
            type: 'text',
            title: 'Official Name',
            APIKey: 'officialName',
            formControl: 'officialName',
            required: true,
            translatable: true,
          },

          {
            type: 'resource',
            title: 'Headquarter Country',
            formControl: 'headquarterscountry',
            APIKey: 'headquarterscountry',
            search: new CountryConfig(this.atom),
            required: true,
            noCols: true,
          },
          {
            type: 'text',
            title: 'Didomi ID',
            formControl: 'didomiConsentId',
            APIKey: 'didomiConsentId',
            translatable: false,
            required: false,
            hint: 'Must be 8 characters',
          },
          {
            type: 'text',
            APIKey: 'vatNumber',
            formControl: 'vatNumber',
            title: 'Vat Number',
            hint: 'Max 100 characters',
          },
          {
            type: 'resource',
            title: 'Parent Firm',
            formControl: 'parentfirm',
            APIKey: 'parentfirm',
            search: new FirmConfig(this.atom),
            noCols: true,
          },
        ],
      },
      {
        title: 'Jobs',
        formGroup: 'jobs',
        options: {
          noActions: true,
        },
        list: [
          {
            type: 'activity',
            title: 'Activities',
            APIKey: 'jobs',
            formControl: 'jobs',
            noCols: true,
          },
        ],
      },
      {
        title: 'Contacts',
        formGroup: 'contacts',
        options: {
          noActions: true,
        },
        list: [
          {
            type: 'ownResource',
            required: false,
            resource: {
              APIKey: 'contacts',
              config: new ContactConfig(),
              multiple: true,
            },
          },
        ],
      },
    ],
  };
  resource = Firm;
  resourceName = 'Firm';
  resourceKey = 'firms';
  resourceKeySingle = 'firm';
  includes = [
    'headquarterscountry',
    'parentfirm.headquarterscountry',
    'contacts.enumtype',
    'jobs.internetserviceproviders.job',
    'jobs.internetserviceproviders.country',
    'jobs.mobilenetworkoperators.job',
    'jobs.mobilenetworkoperators.country',
    'jobs.paymentmethods.job',
    'jobs.firm.headquarterscountry',
    'jobs.activity',
    'trademarks',
  ];
  filters = [
    new NumberFilter('id', 'ID'),
    new StringFilter('name', 'Name', true),
    new StringFilter('headquarterscountry.id', 'Country ID'),
    new StringFilter('headquarterscountry.name', 'Country name'),
    new NumberFilter('parentfirm.id', 'Parent firm ID'),
    new StringFilter('parentfirm.name', 'Parent firm name'),
    new NumberFilter('jobs.id', 'Job ID'),
    new NumberFilter('contracts.id', 'Contract ID'),
    new ResourceListFilter(
      this.atom,
      Activity,
      'jobs.activity.id',
      'Activities'
    ),
  ];
  constructor(private atom: AtomService) {}
}
export class IPRangeConfig implements ResourceConfig {
  hosted: string | undefined = 'firm';
  hostedFilters = {operation: 'equal', value: 'job', path: 'jobs'};
  tables = {
    includes: [],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main' }],
        header: 'ID',
        displayed: true,
        sort: 'id',
      },
      {
        columnDef: 'cidr',
        displayedDatas: [
          {
            path: ['cidr'],
            class: 'mat-body-2',
          },
        ],
        header: 'CIDR',
        displayed: true,
        sort: 'cidr',
        link: {
          detail: true,
        },
      },
      {
        columnDef: 'origin',
        displayedDatas: [
          {
            path: ['origin'],
            class: 'mat-body-2',
          },
        ],
        header: 'Origin',
        displayed: true,
        sort: 'origin',
        link: {
          detail: true,
        },
      },
    ]
  };
  altResourceCreation = {
    config: new JobsIPRangesVersionsConfig(this.atom),
    link: {
      formControl: 'job',
      value: 'jobs',
      filter: [
        {
          key: 'activity.id',
          fixValue: '2',
        },
      ],
    },
    uniq: true,
  };
  resource = JobsIpRange;
  includes = ['job.firm'];
  filters = [
    new NumberFilter('job.id', 'End User Provider ID'),
  ];

  constructor(private atom: AtomService) {}
}
